import { useEffect, useState } from 'react';
import { Plus, Search, UserPlus, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useMeetingContacts } from '../../hooks/useMeetingLog';
import type { AttendeeInput, MeetingContact } from '../../lib/meetingLogApi';
import { Select } from '../ui/Select';

/**
 * One external participant in the meeting form: either an existing directory
 * contact (contactId set) or a person typed in inline (created server-side and
 * deduplicated by email / name + company).
 */
export interface AttendeeDraft {
  key: string;
  contactId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  role: string;
  clientId: string;
}

export function draftFromContact(c: MeetingContact): AttendeeDraft {
  return {
    key: `c:${c.id}`,
    contactId: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email ?? '',
    phone: c.phone ?? '',
    companyName: c.companyName ?? '',
    role: c.role ?? '',
    clientId: c.clientId ?? '',
  };
}

export function draftToInput(d: AttendeeDraft): AttendeeInput {
  if (d.contactId) return { contactId: d.contactId };
  return {
    firstName: d.firstName.trim(),
    lastName: d.lastName.trim() || null,
    email: d.email.trim() || null,
    phone: d.phone.trim() || null,
    companyName: d.companyName.trim() || null,
    role: d.role.trim() || null,
    clientId: d.clientId || null,
  };
}

function draftName(d: AttendeeDraft): string {
  return [d.firstName, d.lastName]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ');
}

const EMPTY_NEW: Omit<AttendeeDraft, 'key'> = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  role: '',
  clientId: '',
};

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

interface Props {
  value: AttendeeDraft[];
  onChange: (next: AttendeeDraft[]) => void;
  clients: { id: string; name: string }[];
  /** Pre-selected client for new people (the meeting's client). */
  defaultClientId?: string;
}

export function AttendeesEditor({ value, onChange, clients, defaultClientId }: Props) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Omit<AttendeeDraft, 'key'>>({
    ...EMPTY_NEW,
    clientId: defaultClientId ?? '',
  });
  const [draftError, setDraftError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(h);
  }, [query]);

  const { data: results } = useMeetingContacts(
    { search: debounced, limit: 8 },
    debounced.length >= 2,
  );
  const selectedIds = new Set(value.map((v) => v.contactId).filter(Boolean));
  const suggestions = (results?.data ?? []).filter((c) => !selectedIds.has(c.id));

  function addExisting(c: MeetingContact) {
    onChange([...value, draftFromContact(c)]);
    setQuery('');
    setDebounced('');
  }

  function remove(key: string) {
    onChange(value.filter((v) => v.key !== key));
  }

  function commitNew() {
    if (!draft.firstName.trim()) {
      setDraftError(t('meetings.attendees.firstNameRequired'));
      return;
    }
    onChange([...value, { ...draft, key: `n:${Date.now()}:${Math.random()}` }]);
    setDraft({ ...EMPTY_NEW, clientId: defaultClientId ?? '' });
    setDraftError(null);
    setAdding(false);
  }

  return (
    <div className="space-y-2">
      {/* Selected people */}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {value.map((d) => (
            <li
              key={d.key}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-3 pr-1.5 text-sm text-gray-800"
            >
              <span>
                {draftName(d)}
                {d.companyName.trim() && (
                  <span className="text-gray-500"> · {d.companyName.trim()}</span>
                )}
                {!d.contactId && (
                  <span className="ml-1 rounded bg-brand-50 px-1 text-[10px] font-medium uppercase text-brand-700">
                    {t('meetings.attendees.newBadge')}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => remove(d.key)}
                className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                aria-label={t('common.remove')}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Search existing */}
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('meetings.attendees.search')}
          className={`${inputCls} pl-9`}
        />
        {debounced.length >= 2 && (
          <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {suggestions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                {t('meetings.attendees.noResults')}
              </li>
            ) : (
              suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => addExisting(c)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <span className="min-w-0 truncate">
                      <span className="font-medium text-gray-900">{c.fullName}</span>
                      {(c.companyName || c.client) && (
                        <span className="text-gray-500"> · {c.companyName ?? c.client?.name}</span>
                      )}
                    </span>
                    {c.email && (
                      <span className="shrink-0 truncate text-xs text-gray-400">{c.email}</span>
                    )}
                  </button>
                </li>
              ))
            )}
            <li className="border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  const [first, ...rest] = query.trim().split(/\s+/);
                  setDraft((d) => ({ ...d, firstName: first ?? '', lastName: rest.join(' ') }));
                  setAdding(true);
                  setQuery('');
                  setDebounced('');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-700 hover:bg-gray-50"
              >
                <UserPlus size={14} /> {t('meetings.attendees.createFromQuery', { name: query })}
              </button>
            </li>
          </ul>
        )}
      </div>

      {/* New person inline */}
      {adding ? (
        <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.firstName')}
              </label>
              <input
                className={inputCls}
                value={draft.firstName}
                onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.lastName')}
              </label>
              <input
                className={inputCls}
                value={draft.lastName}
                onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.email')}
              </label>
              <input
                type="email"
                className={inputCls}
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.phone')}
              </label>
              <input
                className={inputCls}
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.company')}
              </label>
              <input
                className={inputCls}
                value={draft.companyName}
                onChange={(e) => setDraft({ ...draft, companyName: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('meetings.attendees.role')}
              </label>
              <input
                className={inputCls}
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Select
                label={t('meetings.attendees.client')}
                searchable
                clearable
                value={draft.clientId}
                onChange={(v) => setDraft({ ...draft, clientId: v })}
                placeholder={t('meetings.attendees.noClient')}
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            </div>
          </div>
          {draftError && <p className="text-xs text-red-600">{draftError}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraftError(null);
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={commitNew}
              className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={14} /> {t('meetings.attendees.add')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:underline"
        >
          <UserPlus size={15} /> {t('meetings.attendees.new')}
        </button>
      )}
    </div>
  );
}
